import React, { useState, useEffect, useRef, useMemo } from "react";
import { Box, Typography, TextField, Paper, Autocomplete, CircularProgress, Tabs, Tab, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, Switch, FormControlLabel, IconButton, Tooltip, Snackbar, Dialog, DialogTitle, DialogContent } from "@mui/material";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import axiosInstance from "../axiosInstance"; // Make sure this path is correct for your project
import html2canvas from "html2canvas";
import LogAgents from "./LogAgents";
import { getRememberedTenantId, rememberTenantId } from "../utils/tenantSelectionCookie";
// Note: We'll load the xlsx library dynamically from a CDN to parse Excel files

// Typing animation styles
const typingBubbleStyles = `
  @keyframes blink {
    0% { opacity: .2; }
    20% { opacity: 1; }
    100% { opacity: .2; }
  }
  .typing-dot {
    animation-name: blink;
    animation-duration: 1.4s;
    animation-iteration-count: infinite;
    animation-fill-mode: both;
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: #666;
    margin-right: 3px;
  }
  .typing-dot:nth-child(2) { animation-delay: .2s; }
  .typing-dot:nth-child(3) { animation-delay: .4s; }
`;

const OPENAI_PRICING = {
  "gpt-4o": {
    input: 2.50, // per 1M tokens
    output: 10.00 // per 1M tokens
  },
  "gpt-4o-mini": {
    input: 0.15, // per 1M tokens
    output: 0.60 // per 1M tokens
  },
  "gpt-4.1": {
    input: 2.00, // per 1M tokens
    output: 8.00 // per 1M tokens
  },
  "gpt-4.1-mini": {
    input: 0.40, // per 1M tokens
    output: 1.60 // per 1M tokens
  },
  "gpt-5": {
    input: 1.25, // per 1M tokens (estimated)
    output: 10.00 // per 1M tokens (estimated)
  },
  "gpt-5-mini": {
    input: 0.25, // per 1M tokens (estimated)
    output: 2.00 // per 1M tokens (estimated)
  },
  "gpt-5-nano": {
    input: 0.05, // per 1M tokens (estimated)
    output: 0.40 // per 1M tokens (estimated)
  }
};

const calculateCost = (model, promptTokens, completionTokens) => {
  const normalizedPromptTokens = promptTokens || 0;
  const normalizedCompletionTokens = completionTokens || 0;
  if (!model || (normalizedPromptTokens === 0 && normalizedCompletionTokens === 0)) {
    return { usd: 0 };
  }
  const normalizedModel = model.toLowerCase().replace(/_/g, '-');
  let pricing = null;
  if (OPENAI_PRICING[normalizedModel]) {
    pricing = OPENAI_PRICING[normalizedModel];
  } else {
    for (const [key, value] of Object.entries(OPENAI_PRICING)) {
      if (normalizedModel.includes(key)) {
        pricing = value;
        break;
      }
    }
  }
  if (!pricing) pricing = OPENAI_PRICING["gpt-4o"];
  const inputCost = (normalizedPromptTokens / 1000000) * pricing.input;
  const outputCost = (normalizedCompletionTokens / 1000000) * pricing.output;
  return { usd: inputCost + outputCost };
};

const ChatTester = ({ selectedTenant: selectedTenantId = "", setSelectedTenant: setSelectedTenantId }) => {
  // const iframeRef = useRef(null);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [allTenants, setAllTenants] = useState([]);
  const [loadingTenants, setLoadingTenants] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const chatContainerRef = useRef(null);
  const [tabValue, setTabValue] = useState(0);
  const [testCases, setTestCases] = useState([]);
  const [fileName, setFileName] = useState("");
  const [currentTestIndex, setCurrentTestIndex] = useState(-1);
  const [isTestRunning, setIsTestRunning] = useState(false);
  const [isAutoRunning, setIsAutoRunning] = useState(false);
  const [allowScreenshots, setAllowScreenshots] = useState(false);
  const [copyStatus, setCopyStatus] = useState({ open: false, message: "" });
  const [logModalOpen, setLogModalOpen] = useState(false);
  const [selectedLogTraceId, setSelectedLogTraceId] = useState("");

  const cumulativeTotalCost = useMemo(() => {
    return testCases.reduce((sum, tc) =>
      sum + (tc.costAgentTester || 0) + (tc.costWidget || 0), 0
    );
  }, [testCases]);

  // Refs to avoid stale closures inside effects
  const currentTestIndexRef = useRef(-1);
  const isTestRunningRef = useRef(false);
  const isAutoRunningRef = useRef(false);
  const allowScreenshotsRef = useRef(false);
  const testCasesRef = useRef([]);
  // Helper: Recursively searches through shadow DOMs to find an element
  const findInShadowDOM = (root, selector) => {
    if (!root) return null;
    const found = root.querySelector(selector);
    if (found) return found;
    const allElements = root.querySelectorAll('*');
    for (const el of allElements) {
      if (el.shadowRoot) {
        const result = findInShadowDOM(el.shadowRoot, selector);
        if (result) return result;
      }
    }
    return null;
  };

  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.type = "text/css";
    styleSheet.innerText = typingBubbleStyles;
    document.head.appendChild(styleSheet);

    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  // Effect to fetch the list of tenants when the component loads
  useEffect(() => {
    const fetchTenants = async () => {
      setLoadingTenants(true);
      try {
        const response = await axiosInstance.get("/tenant/alltenants");
        const tenantList = response.data || [];
        setAllTenants(tenantList);

        const rememberedTenantId = selectedTenantId || getRememberedTenantId();
        if (rememberedTenantId) {
          const rememberedTenant = tenantList.find((tenant) => tenant.id === rememberedTenantId);
          if (rememberedTenant) {
            setSelectedTenant(rememberedTenant);
          }
        }
      } catch (error) {
        console.error("Error fetching tenants for dropdown:", error);
      } finally {
        setLoadingTenants(false);
      }
    };
    fetchTenants();
  }, [selectedTenantId]);

  useEffect(() => {
    if (!selectedTenantId || allTenants.length === 0) return;

    const matchedTenant = allTenants.find((tenant) => tenant.id === selectedTenantId);
    if (matchedTenant && matchedTenant.id !== selectedTenant?.id) {
      setSelectedTenant(matchedTenant);
    }
  }, [selectedTenantId, allTenants, selectedTenant]);

  // Recursively searches through shadow DOMs to find an element
  const captureScreenshot = (tenantName) => {
    console.log('[ChatTester] Waiting 1s before screenshot...');
    const element = document.getElementById('widget-container');
    if (!element) {
      console.warn('[ChatTester] widget-container not found for screenshot');
      return;
    }

    setTimeout(() => {
      html2canvas(element, {
        useCORS: true,
        allowTaint: true,
        logging: false,
        scale: 2, // Use a consistent high scale for better quality
        backgroundColor: '#f9f9f9',
        onclone: (clonedDoc) => {
          const clonedElement = clonedDoc.getElementById('widget-container');
          if (clonedElement) {
            clonedElement.style.height = 'auto';
            clonedElement.style.overflow = 'visible';
          }
        }
      }).then(canvas => {
        const image = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.href = image;
        link.download = `ai-chat-${tenantName || 'tenant'}-${new Date().getTime()}.png`;
        link.click();
        console.log('[ChatTester] Screenshot captured for AI message');
      }).catch(err => {
        console.error('[ChatTester] Screenshot failed:', err);
      });
    }, 1000);
  };

  // Read-only polling of prz-ai-chat-history — written exclusively by the widget
  const lastHistoryCountRef = useRef(0);
  const lastHistoryRawRef = useRef('');

  useEffect(() => {
    const interval = setInterval(() => {
      const historyRaw = localStorage.getItem('prz-ai-chat-history') || '';

      // Skip if nothing changed — avoids unnecessary re-renders
      if (historyRaw === lastHistoryRawRef.current) return;
      lastHistoryRawRef.current = historyRaw;

      if (!historyRaw) {
        setChatHistory([]);
        lastHistoryCountRef.current = 0;
        return;
      }

      let history;
      try {
        history = JSON.parse(historyRaw);
      } catch (e) {
        console.error('[ChatTester] Failed to parse chat history:', e);
        return;
      }

      // Mirror exactly what the widget wrote — no modifications
      setChatHistory(history);

      // Screenshot on new AI messages
      if (history.length > lastHistoryCountRef.current) {
        const newMessages = history.slice(lastHistoryCountRef.current);
        if (newMessages.some(msg => !msg.isSender)) {
          if (allowScreenshotsRef.current) {
            captureScreenshot(selectedTenant?.name);
          }
        }
      }

      // Always update count (handles resets too)
      lastHistoryCountRef.current = history.length;
    }, 1000);

    return () => clearInterval(interval);
  }, [selectedTenant]);

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }

    // Use refs to avoid stale closure — state values may be outdated here
    if (isTestRunningRef.current && currentTestIndexRef.current >= 0) {
      const lastMsg = chatHistory[chatHistory.length - 1];
      if (lastMsg && !lastMsg.isSender) {
        // AI responded — trigger API validation
        const idx = currentTestIndexRef.current;
        const userInput = chatHistory[chatHistory.length - 2]?.content || "";
        const systemResponse = lastMsg.content;

        // Mark as not running so we don't re-trigger while API is pending
        isTestRunningRef.current = false;
        setIsTestRunning(false);

        const products = lastMsg.responsePayload || [];
        const traceId = lastMsg.traceId || "";
        validateResponse(idx, userInput, systemResponse, products, traceId);
      }
    }
  }, [chatHistory]);

  // Keep refs in sync
  useEffect(() => {
    testCasesRef.current = testCases;
  }, [testCases]);

  useEffect(() => {
    allowScreenshotsRef.current = allowScreenshots;
  }, [allowScreenshots]);

  // Effect to manage the widget script injection
  useEffect(() => {
    if (selectedTenant) {
      document.querySelector('#preezie-ai-app')?.remove();
      window.defaultGBWidgetRendered = false;
      window.preezieGBPageScriptExecuted = false;
      window.mountPreezieGBApp = undefined;
      window.sendAIShoppingAssistantMessage = undefined;

      const widgetPreguide = `${process.env.REACT_APP_WIDGET_URL}/preguide_ai.js`;
      const testTenantId = selectedTenant.id;

      const defaultContainer = document.createElement('div');
      defaultContainer.id = 'preezie-ai-app';
      document.body.appendChild(defaultContainer);

      const defaultScript = document.createElement('script');
      defaultScript.src = widgetPreguide;
      defaultScript.setAttribute('data-tenantid', testTenantId);
      defaultScript.defer = true;
      defaultContainer.appendChild(defaultScript);

      return () => {
        // Cleanup happens when selectedTenant changes or component unmounts
      };
    }
  }, [selectedTenant]);

  /**
   * Validates the AI response by calling the test-agent-response API
   */
  const validateResponse = async (index, userInput, systemResponse, products = [], traceId = "") => {
    console.log(`[ChatTester] Validating response for test ${index + 1}...`);
    try {
      // Set temporary status to show it's validating
      setTestCases(prev => prev.map((tc, i) =>
        i === index ? { ...tc, pass: "Validating..." } : tc
      ));

      const response = await axiosInstance.post("/agents/test-agent-response", {
        UserInput: userInput,
        SystemResponse: systemResponse,
        TenantId: selectedTenant?.id,
        Products: products,
        TraceId: traceId
      });

      let finalTraceId = traceId;
      let totalCostUsd = 0;

      if (response.data && response.data.data) {
        let parsedData;
        if (typeof response.data.data === 'string') {
          try {
            parsedData = JSON.parse(response.data.data);
          } catch (parseErr) {
            console.error("[ChatTester] Failed to parse validation data as string:", parseErr);
            setTestCases(prev => prev.map((tc, i) =>
              i === index ? { ...tc, pass: "Parse Error" } : tc
            ));
            return;
          }
        } else {
          parsedData = response.data.data;
        }

        const agentTesterTraceId = parsedData.TraceIdAgentTester || parsedData.traceIdAgentTester || "";
        finalTraceId = parsedData.TraceIdWidget || parsedData.traceIdWidget || parsedData.traceId || parsedData.TraceId || traceId;

        let costAgentTester = 0;
        let costWidget = 0;

        // Helper to get total cost for a trace
        const fetchTraceCost = async (tid) => {
          if (!tid) return 0;
          let total = 0;
          try {
            const traceRes = await axiosInstance.get(`/agents/trace/${tid}`);
            if (traceRes.data?.data && Array.isArray(traceRes.data.data)) {
              traceRes.data.data.forEach(agent => {
                const model = agent.promptContent?.llmModel || 'unknown';
                const promptTokens = agent.promptContent?.promptTokensFromResponse || 0;
                const completionTokens = agent.promptContent?.completionTokensFromResponse || 0;
                if (promptTokens > 0 || completionTokens > 0) {
                  const cost = calculateCost(model, promptTokens, completionTokens);
                  total += cost.usd;
                }
              });
            }
          } catch (err) {
            console.error(`[ChatTester] Failed to fetch cost for trace ${tid}:`, err);
          }
          return total;
        };

        // Fetch costs in parallel
        const [cTester, cWidget] = await Promise.all([
          fetchTraceCost(agentTesterTraceId),
          fetchTraceCost(finalTraceId)
        ]);

        costAgentTester = cTester;
        costWidget = cWidget;

        setTestCases(prev => prev.map((tc, i) =>
          i === index ? {
            ...tc,
            pass: parsedData.Pass || parsedData.pass || "No Status",
            notes: parsedData.Notes || parsedData.notes || tc.notes,
            traceId: finalTraceId,
            traceIdAgentTester: agentTesterTraceId,
            fixNotes: parsedData.FixNotes || parsedData.fixNotes || tc.fixNotes,
            costAgentTester: costAgentTester,
            costWidget: costWidget
          } : tc
        ));
        console.log(`[ChatTester] Validation complete for test ${index + 1}: ${parsedData.pass || parsedData.Pass}`);
      } else {
        setTestCases(prev => prev.map((tc, i) =>
          i === index ? { ...tc, pass: "No Data" } : tc
        ));
      }

      // If auto-running and NOT manually stopped, trigger the next test automatically after the API returns
      if (isAutoRunningRef.current) {
        setTimeout(() => {
          // Check again inside timeout to ensure we haven't stopped in the meantime
          if (isAutoRunningRef.current) {
            handleRunNext();
          }
        }, 2000); // 2s delay between tests
      }
    } catch (error) {
      console.error("[ChatTester] Validation API call failed:", error);
      setTestCases(prev => prev.map((tc, i) =>
        i === index ? { ...tc, pass: "API Error" } : tc
      ));

      if (isAutoRunningRef.current) {
        setTimeout(() => {
          if (isAutoRunningRef.current) {
            handleRunNext();
          }
        }, 2000);
      }
    }
  };

  /**
   * Recursively collects ALL elements matching a selector across shadow DOMs.
   */
  const findAllInShadowDOM = (root, selector, results = []) => {
    root.querySelectorAll(selector).forEach(el => results.push(el));
    root.querySelectorAll('*').forEach(el => {
      if (el.shadowRoot) findAllInShadowDOM(el.shadowRoot, selector, results);
    });
    return results;
  };

  /**
   * Closes the widget by finding the close button via class name inside the shadow DOM.
   */
  const closeWidget = () => {
    console.log('[ChatTester] Attempting to close widget...');

    // Walk every shadow root and find all elements, then filter by class
    const findButtonByClass = (root, className) => {
      const all = root.querySelectorAll('*');
      for (const el of all) {
        if (el.classList && el.classList.contains(className)) {
          return el;
        }
        if (el.shadowRoot) {
          const found = findButtonByClass(el.shadowRoot, className);
          if (found) return found;
        }
      }
      return null;
    };

    // Try the known class first — update this string if it changes after a build
    const knownClass = 'wCcKh';
    let btn = findButtonByClass(document, knownClass);
    console.log(`[ChatTester] Button found by class "${knownClass}":`, btn);

    // Fallback: button inside [ischatmodalopen="true"]
    if (!btn) {
      const modal = findInShadowDOM(document, '[ischatmodalopen="true"]');
      btn = modal?.querySelector('button') || null;
      console.log('[ChatTester] Button found via [ischatmodalopen]:', btn);
    }

    if (!btn) {
      console.error('[ChatTester] Could not find close button.');
      return;
    }

    // Simulate a real click — plain .click() often fails for React/shadow DOM elements
    console.log('[ChatTester] Simulating click on:', btn);
    btn.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true, composed: true, view: window }));
    btn.dispatchEvent(new MouseEvent('mouseup', { bubbles: true, cancelable: true, composed: true, view: window }));
    btn.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, composed: true, view: window }));
  };

  const runTestCase = (index) => {
    // Prevent double-firing if a test is already in progress
    if (isTestRunningRef.current) {
      console.warn('[ChatTester] A test is already running, ignoring call.');
      return;
    }

    // Find next valid (non-empty, not already passed) test case from this index onward
    let targetIndex = index;
    while (targetIndex < testCasesRef.current.length) {
      const tc = testCasesRef.current[targetIndex];
      if (!tc) break;
      if (!tc.question || tc.question.trim() === "") {
        console.log(`[ChatTester] Skipping test ${targetIndex + 1} — empty question`);
        targetIndex++;
        continue;
      }
      if (tc.pass && tc.pass.trim() !== "") {
        console.log(`[ChatTester] Skipping test ${targetIndex + 1} — already run (pass: ${tc.pass})`);
        targetIndex++;
        continue;
      }
      break; // found a valid unrun case
    }

    if (targetIndex >= testCasesRef.current.length) {
      console.log("[ChatTester] No more unrun test cases.");
      isTestRunningRef.current = false;
      setIsTestRunning(false);
      isAutoRunningRef.current = false;
      setIsAutoRunning(false);
      return;
    }

    const testCase = testCasesRef.current[targetIndex];
    console.log(`[ChatTester] Running test ${targetIndex + 1}: ${testCase.question}`);

    currentTestIndexRef.current = targetIndex;
    isTestRunningRef.current = true;
    setCurrentTestIndex(targetIndex);
    setIsTestRunning(true);

    if (window.sendAIShoppingAssistantMessage) {
      // Set status to "Processing..."
      setTestCases(prev => prev.map((tc, i) =>
        i === targetIndex ? { ...tc, pass: "Processing..." } : tc
      ));

      window.sendAIShoppingAssistantMessage({
        message: testCase.question,
        trigger: 'default'
      });

      // Wait 2 seconds, then attempt to close the widget
      setTimeout(() => {
        closeWidget();
      }, 2000);
    } else {
      console.error("[ChatTester] window.sendAIShoppingAssistantMessage not found");
      isTestRunningRef.current = false;
      setIsTestRunning(false);
    }
  };

  const handleRunTests = () => {
    // Enable auto-running
    setIsAutoRunning(true);
    isAutoRunningRef.current = true;

    // Reset all pass statuses and run from the beginning
    setTestCases(prev => {
      const reset = prev.map(tc => ({ ...tc, pass: "" }));
      testCasesRef.current = reset;
      return reset;
    });
    setTimeout(() => runTestCase(0), 0);
  };

  const handleRunNext = () => {
    // If we've been stopped, don't proceed
    if (isAutoRunning && !isAutoRunningRef.current) {
      setIsAutoRunning(false);
      return;
    }
    // Find next unrun case after currentTestIndex
    const nextIndex = currentTestIndexRef.current + 1;
    runTestCase(nextIndex);
  };

  const handleStopTests = () => {
    console.log("[ChatTester] Stopping automated tests...");
    setIsAutoRunning(false);
    isAutoRunningRef.current = false;
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      const loadXLSX = async () => {
        if (window.XLSX && typeof window.XLSX.read === 'function') {
          return window.XLSX;
        }
        return new Promise((resolve, reject) => {
          const script = document.createElement("script");
          script.src = "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";
          script.onload = () => resolve(window.XLSX);
          script.onerror = reject;
          document.head.appendChild(script);
        });
      };

      try {
        const XLSX = await loadXLSX();
        const reader = new FileReader();
        reader.onload = (e) => {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);

          const mappedData = jsonData.map((row, index) => ({
            id: index,
            intent: row.Intent || "",
            scenario: row.Scenario || "",
            productName: row["Product Name"] || "",
            question: row.Question || "",
            pass: row.Pass || "",
            notes: row.Notes || "",
            traceId: row["Trace ID"] || row.TraceId || "",
            traceIdAgentTester: "",
            fixNotes: row.FixNotes || ""
          }));

          setTestCases(mappedData);
          setFileName(file.name);
          setCurrentTestIndex(-1);
          setIsTestRunning(false);
          setIsAutoRunning(false);
        };
        reader.readAsArrayBuffer(file);
        console.log("[ChatTester] File processing started:", file.name);
      } catch (err) {
        console.error("[ChatTester] Failed to load XLSX:", err);
      }
    }
  };

  const handleCopy = (text, label) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopyStatus({ open: true, message: `${label} copied to clipboard!` });
  };

  const handleOpenLogModal = (traceId) => {
    if (!traceId) return;
    setSelectedLogTraceId(traceId);
    setLogModalOpen(true);
  };

  return (
    <Box
      sx={{
        maxWidth: 1400,
        mx: "auto",
        px: { xs: 1, md: 2 },
        py: { xs: 1, md: 2 },
      }}
    >
      <Box
        sx={{
          borderRadius: { xs: 4, md: 6 },
          background:
            "radial-gradient(circle at top center, rgba(255, 191, 154, 0.28), rgba(255,255,255,0.94) 42%), linear-gradient(180deg, rgba(255, 248, 243, 0.96), rgba(255, 255, 255, 0.92))",
          boxShadow: "0 24px 60px rgba(236, 123, 55, 0.10)",
          border: "1px solid rgba(255, 132, 76, 0.10)",
          p: { xs: 2, md: 4 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            mb: 3,
          }}
        >
          <Box
            sx={{
              width: 50,
              height: 50,
              borderRadius: 2.5,
              background: "linear-gradient(180deg, #ffb58d 0%, #ff8b47 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 16px 30px rgba(255, 139, 71, 0.24)",
              color: "#fff",
              flexShrink: 0,
            }}
          >
            <ShoppingCartIcon />
          </Box>
          <Box>
            <Typography
              variant="h3"
              sx={{
                fontSize: { xs: "2rem", md: "2.55rem" },
                fontWeight: 700,
                color: "#202531",
                lineHeight: 1.05,
              }}
            >
              Widget Tester
            </Typography>
            <Typography
              sx={{
                mt: 0.75,
                color: "rgba(32, 37, 49, 0.72)",
                fontSize: "0.98rem",
              }}
            >
              Test the storefront widget and validate AI responses across tenants.
            </Typography>
          </Box>
        </Box>

        <Paper
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 3,
            border: "1px solid rgba(255, 132, 76, 0.10)",
            backgroundColor: "rgba(255, 255, 255, 0.88)",
            boxShadow: "0 20px 44px rgba(236, 123, 55, 0.08)",
            p: { xs: 2, md: 3 },
          }}
        >
          <Autocomplete
            options={allTenants}
            getOptionLabel={(option) => option.name || ""}
            value={selectedTenant || null}
            onChange={(event, newValue) => {
              setSelectedTenant(newValue);
              rememberTenantId(newValue?.id || "");
              if (typeof setSelectedTenantId === "function") {
                setSelectedTenantId(newValue?.id || "");
              }
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            loading={loadingTenants}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Tenant to Test"
                variant="outlined"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loadingTenants ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    minHeight: 58,
                    borderRadius: 2.25,
                    backgroundColor: "rgba(255, 252, 249, 0.96)",
                    "& fieldset": {
                      borderColor: "rgba(255, 132, 76, 0.18)",
                    },
                    "&:hover fieldset": {
                      borderColor: "rgba(255, 132, 76, 0.36)",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#ff8b47",
                      boxShadow: "0 0 0 4px rgba(255, 139, 71, 0.12)",
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#ff8b47",
                  },
                }}
              />
            )}
            sx={{ width: "100%" }}
          />
        </Paper>

        {selectedTenant && (
          <Paper
            elevation={0}
            sx={{
              height: "75vh",
              display: "flex",
              flexDirection: "column",
              borderRadius: 3.5,
              border: "1px solid rgba(255, 132, 76, 0.10)",
              backgroundColor: "rgba(255, 255, 255, 0.88)",
              boxShadow: "0 22px 48px rgba(236, 123, 55, 0.08)",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                px: { xs: 2, md: 3 },
                pt: { xs: 2, md: 2.5 },
                borderBottom: "1px solid rgba(255, 132, 76, 0.10)",
              }}
            >
              <Tabs
                value={tabValue}
                onChange={(e, val) => setTabValue(val)}
                aria-label="chat tester tabs"
                sx={{
                  "& .MuiTab-root": {
                    minHeight: 54,
                    fontWeight: 600,
                    color: "rgba(32, 37, 49, 0.62)",
                    textTransform: "none",
                  },
                  "& .Mui-selected": {
                    color: "#ff7a30 !important",
                  },
                  "& .MuiTabs-indicator": {
                    height: 3,
                    borderRadius: 999,
                    backgroundColor: "#ff7a30",
                  },
                }}
              >
                <Tab label="Widget Preview" />
                <Tab label="Test Cases" />
              </Tabs>
            </Box>

            {/* TAB 1: WIDGET PREVIEW */}
            {tabValue === 0 && (
              <Box sx={{ p: { xs: 2, md: 3 }, flexGrow: 1, display: "flex", flexDirection: "row", gap: 3, overflow: "hidden" }}>
              {/* Left Column: Chat Widget (40% width) */}
              <Box sx={{ width: testCases.length > 0 ? "40%" : "100%", display: "flex", flexDirection: "column", minWidth: 0 }}>
                <Typography variant="h6" gutterBottom>
                  Widget Preview: {selectedTenant.name}
                </Typography>
                <Box
                  id="widget-container"
                  ref={chatContainerRef}
                  style={{
                    flexGrow: 1,
                    border: "1px solid #ccc",
                    position: "relative",
                    borderRadius: "8px",
                    overflowY: "auto",
                    padding: "16px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                    backgroundColor: "#f9f9f9",
                  }}
                >
                  {chatHistory.length === 0 ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                      <Typography color="textSecondary">No messages yet. Waiting for chat to start...</Typography>
                    </Box>
                  ) : (
                    <>
                      {chatHistory.map((msg, index) => (
                        <Box
                          key={index}
                          sx={{
                            alignSelf: msg.isSender ? "flex-end" : "flex-start",
                            maxWidth: "80%",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: msg.isSender ? "flex-end" : "flex-start",
                          }}
                        >
                          <Typography variant="caption" sx={{ mb: 0.5, color: "#666", fontWeight: "bold" }}>
                            {msg.isSender ? "User" : "AI"}
                          </Typography>
                          {/* Products Carousel */}
                          {!msg.isSender && msg.responsePayload && Array.isArray(msg.responsePayload) && msg.responsePayload.some(p => p !== null) && (
                            <Box
                              sx={{
                                display: 'flex',
                                overflowX: 'auto',
                                gap: 1.5,
                                py: 1,
                                mb: 1,
                                width: '100%',
                                msOverflowStyle: 'none',
                                scrollbarWidth: 'none',
                                '&::-webkit-scrollbar': { display: 'none' }
                              }}
                            >
                              {msg.responsePayload.filter(p => p !== null).map((product, pIdx) => (
                                <Paper
                                  key={pIdx}
                                  elevation={2}
                                  sx={{
                                    minWidth: 160,
                                    maxWidth: 160,
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    backgroundColor: '#fff',
                                    border: '1px solid #eee'
                                  }}
                                >
                                  {/* Top Buttons */}
                                  <Box sx={{ display: 'flex', gap: 0.5, p: 0.5 }}>
                                    <Button variant="outlined" size="small" sx={{ fontSize: '0.6rem', py: 0, height: 18, minWidth: 0, flex: 1, borderColor: '#333', color: '#333' }}>Similar</Button>
                                    <Button variant="outlined" size="small" sx={{ fontSize: '0.6rem', py: 0, height: 18, minWidth: 0, flex: 1, borderColor: '#333', color: '#333' }}>Compare</Button>
                                  </Box>

                                  {/* Product Image */}
                                  <Box sx={{ height: 180, backgroundColor: '#f5f5f5', overflow: 'hidden', position: 'relative' }}>
                                    <img
                                      src={product.imageUrl}
                                      alt={product.title}
                                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                  </Box>

                                  {/* Product Info */}
                                  <Box sx={{ p: 1, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontWeight: 'bold',
                                        lineHeight: 1.2,
                                        mb: 1,
                                        fontSize: '0.7rem',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                        height: '2.4em'
                                      }}
                                    >
                                      {product.title}
                                    </Typography>

                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
                                      <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                                        ${product.price}
                                      </Typography>
                                      <IconButton size="small" sx={{ backgroundColor: '#d1b4a1', color: '#fff', '&:hover': { backgroundColor: '#c1a491' }, p: 0.4 }}>
                                        <ShoppingCartIcon sx={{ fontSize: 14 }} />
                                      </IconButton>
                                    </Box>
                                  </Box>
                                </Paper>
                              ))}
                            </Box>
                          )}

                          <Paper
                            elevation={1}
                            sx={{
                              padding: "10px 14px",
                              borderRadius: msg.isSender ? "18px 18px 0 18px" : "18px 18px 18px 0",
                              backgroundColor: msg.isSender ? "#2196f3" : "#fff",
                              color: msg.isSender ? "#fff" : "#333",
                              border: msg.isSender ? "none" : "1px solid #e0e0e0",
                            }}
                          >
                            <Typography variant="body2" style={{ whiteSpace: "pre-wrap" }}>
                              {msg.content}
                            </Typography>
                          </Paper>
                        </Box>
                      ))}

                      {/* AI Typing Indicator */}
                      {chatHistory.length > 0 && chatHistory[chatHistory.length - 1].isSender && (
                        <Box
                          sx={{
                            alignSelf: "flex-start",
                            maxWidth: "80%",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            mt: "8px"
                          }}
                        >
                          <Typography variant="caption" sx={{ mb: 0.5, color: "#666", fontWeight: "bold" }}>
                            AI
                          </Typography>
                          <Paper
                            elevation={1}
                            sx={{
                              padding: "12px 18px",
                              borderRadius: "18px 18px 18px 0",
                              backgroundColor: "#fff",
                              border: "1px solid #e0e0e0",
                              display: "flex",
                              alignItems: "center",
                              minWidth: "60px",
                              justifyContent: "center"
                            }}
                          >
                            <span className="typing-dot"></span>
                            <span className="typing-dot"></span>
                            <span className="typing-dot"></span>
                          </Paper>
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              </Box>

              {/* Right Column: Mini Test Cases Table (Takes remaining 60%) */}
              {testCases.length > 0 && (
                <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Box display="flex" alignItems="baseline" gap={2}>
                      <Typography id="test-cases-summary-label" variant="h6">
                        Test Cases Summary
                      </Typography>
                      <Typography variant="subtitle2" color="primary.main" fontWeight="bold">
                        Total Cumulative Cost: ${cumulativeTotalCost.toFixed(6)}
                      </Typography>
                    </Box>
                    <Box display="flex" gap={2} alignItems="center">
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={allowScreenshots}
                            onChange={(e) => setAllowScreenshots(e.target.checked)}
                            color="primary"
                          />
                        }
                        label={<Typography sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>Take Screenshots</Typography>}
                      />
                      <Box display="flex" gap={1}>
                        {isAutoRunning ? (
                          <Button
                            variant="contained"
                            size="small"
                            color="error"
                            onClick={handleStopTests}
                          >
                            Stop Tests
                          </Button>
                        ) : (
                          <Button
                            variant="contained"
                            size="small"
                            color="primary"
                            onClick={handleRunTests}
                            disabled={isTestRunning}
                          >
                            Run Tests
                          </Button>
                        )}
                        <Button
                          variant="outlined"
                          size="small"
                          color="secondary"
                          onClick={handleRunNext}
                          disabled={isTestRunning || isAutoRunning || currentTestIndex >= testCases.length - 1}
                        >
                          Run Next
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                  <TableContainer component={Paper} variant="outlined" sx={{ flexGrow: 1, overflowX: "auto", overflowY: "auto" }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell style={{ fontWeight: "bold", backgroundColor: "#f5f5f5", fontSize: "0.75rem" }}>Intent</TableCell>
                          <TableCell style={{ fontWeight: "bold", backgroundColor: "#f5f5f5", fontSize: "0.75rem", minWidth: "300px" }}>Question</TableCell>
                          <TableCell style={{ fontWeight: "bold", backgroundColor: "#f5f5f5", fontSize: "0.75rem" }} align="center">Pass</TableCell>
                          <TableCell style={{ fontWeight: "bold", backgroundColor: "#f5f5f5", fontSize: "0.75rem" }}>Trace Id Agent Tester</TableCell>
                          <TableCell style={{ fontWeight: "bold", backgroundColor: "#f5f5f5", fontSize: "0.75rem" }}>TraceId Widget</TableCell>
                          <TableCell style={{ fontWeight: "bold", backgroundColor: "#f5f5f5", fontSize: "0.75rem", minWidth: "140px" }} align="right">Cost Agent Tester (USD)</TableCell>
                          <TableCell style={{ fontWeight: "bold", backgroundColor: "#f5f5f5", fontSize: "0.75rem", minWidth: "120px" }} align="right">Cost Widget (USD)</TableCell>
                          {testCases.some(tc => tc.pass?.toLowerCase() === 'failed' || tc.pass?.toLowerCase() === 'no') && (
                            <TableCell style={{ fontWeight: "bold", backgroundColor: "#f5f5f5", fontSize: "0.75rem", minWidth: "300px" }}>Fix Notes</TableCell>
                          )}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {testCases.map((row) => (
                          <TableRow key={row.id} hover>
                            <TableCell sx={{ fontSize: "0.75rem", py: 1 }}>{row.intent}</TableCell>
                            <TableCell sx={{ fontSize: "0.75rem", py: 1, color: "#1976d2", minWidth: "300px" }}>{row.question}</TableCell>
                            <TableCell align="center" sx={{ py: 1 }}>
                              {row.pass && (
                                <Chip
                                  label={row.pass}
                                  size="small"
                                  sx={{ height: 20, fontSize: "0.65rem" }}
                                  color={
                                    (row.pass.toLowerCase() === 'yes' || row.pass.toLowerCase() === 'passed') ? 'success' :
                                      (row.pass.toLowerCase() === 'no' || row.pass.toLowerCase() === 'failed') ? 'error' :
                                        row.pass === 'Processing...' ? 'info' :
                                          row.pass === 'Validating...' ? 'warning' : 'default'
                                  }
                                />
                              )}
                            </TableCell>
                            <TableCell sx={{ fontSize: "0.7rem", py: 1, fontFamily: "monospace" }}>
                              <Tooltip title="Click to view Agents Trace logs">
                                <Box
                                  sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5, '&:hover': { color: '#1976d2' } }}
                                  onClick={() => handleOpenLogModal(row.traceIdAgentTester)}
                                >
                                  {row.traceIdAgentTester}
                                </Box>
                              </Tooltip>
                            </TableCell>
                            <TableCell sx={{ fontSize: "0.7rem", py: 1, fontFamily: "monospace" }}>
                              <Tooltip title="Click to view Agents Trace logs">
                                <Box
                                  sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5, '&:hover': { color: '#1976d2' } }}
                                  onClick={() => handleOpenLogModal(row.traceId)}
                                >
                                  {row.traceId}
                                </Box>
                              </Tooltip>
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: "0.7rem", py: 1, fontWeight: "bold", minWidth: "140px" }}>
                              {row.costAgentTester !== undefined ? `$${row.costAgentTester.toFixed(6)}` : "-"}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: "0.7rem", py: 1, fontWeight: "bold", minWidth: "120px" }}>
                              {row.costWidget !== undefined ? `$${row.costWidget.toFixed(6)}` : "-"}
                            </TableCell>
                            {testCases.some(tc => tc.pass?.toLowerCase() === 'failed' || tc.pass?.toLowerCase() === 'no') && (
                              <TableCell sx={{ fontSize: "0.7rem", py: 1, color: "#d32f2f", minWidth: "300px" }}>{row.fixNotes}</TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
            )}

            {/* TAB 2: TEST CASES */}
            {tabValue === 1 && (
              <Box sx={{ p: { xs: 2, md: 3 }, flexGrow: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Box display="flex" alignItems="baseline" gap={2}>
                  <Typography variant="h6">Test Cases (Excel)</Typography>
                  <Typography variant="subtitle2" color="primary.main" fontWeight="bold">
                    Total Cumulative Cost: ${cumulativeTotalCost.toFixed(6)}
                  </Typography>
                </Box>
                <Button
                  component="label"
                  variant="contained"
                  startIcon={<CloudUploadIcon />}
                >
                  Upload File (Excel/CSV)
                  <input type="file" hidden accept=".xlsx, .xls, .csv" onChange={handleFileUpload} />
                </Button>
              </Box>

              {fileName && (
                <Typography variant="body2" color="primary" gutterBottom>
                  Loaded: {fileName}
                </Typography>
              )}

              <TableContainer component={Paper} variant="outlined" sx={{ flexGrow: 1, overflowY: "auto" }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell style={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>Intent</TableCell>
                      <TableCell style={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>Scenario</TableCell>
                      <TableCell style={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>Product Name</TableCell>
                      <TableCell style={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>Question</TableCell>
                      <TableCell style={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }} align="center">Pass</TableCell>
                      <TableCell style={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>Notes</TableCell>
                      <TableCell style={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>Trace Id Agent Tester</TableCell>
                      <TableCell style={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>TraceId Widget</TableCell>
                      <TableCell style={{ fontWeight: "bold", backgroundColor: "#f5f5f5", minWidth: "160px" }} align="right">Cost Agent Tester (USD)</TableCell>
                      <TableCell style={{ fontWeight: "bold", backgroundColor: "#f5f5f5", minWidth: "140px" }} align="right">Cost Widget (USD)</TableCell>
                      <TableCell style={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>Fix Notes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {testCases.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                          <Typography color="textSecondary">No test cases loaded. Upload the Excel file to see data.</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      testCases.map((row) => (
                        <TableRow key={row.id} hover>
                          <TableCell sx={{ fontSize: "0.8rem" }}>{row.intent}</TableCell>
                          <TableCell sx={{ fontSize: "0.8rem", maxWidth: "200px" }}>{row.scenario}</TableCell>
                          <TableCell sx={{ fontSize: "0.8rem" }}>{row.productName}</TableCell>
                          <TableCell sx={{ fontSize: "0.8rem", color: "#1976d2", fontWeight: "medium" }}>{row.question}</TableCell>
                          <TableCell align="center">
                            {row.pass && (
                              <Chip
                                label={row.pass}
                                size="small"
                                color={
                                  (row.pass.toLowerCase() === 'yes' || row.pass.toLowerCase() === 'passed') ? 'success' :
                                    (row.pass.toLowerCase() === 'no' || row.pass.toLowerCase() === 'failed') ? 'error' :
                                      row.pass === 'Processing...' ? 'info' :
                                        row.pass === 'Validating...' ? 'warning' : 'default'
                                }
                              />
                            )}
                          </TableCell>
                          <TableCell sx={{ fontSize: "0.80rem", color: "#666" }}>{row.notes}</TableCell>
                          <TableCell sx={{ fontSize: "0.8rem", fontFamily: "monospace" }}>
                            <Tooltip title="Click to view Agents Trace logs">
                              <Box
                                sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5, '&:hover': { color: '#1976d2' } }}
                                onClick={() => handleOpenLogModal(row.traceIdAgentTester)}
                              >
                                {row.traceIdAgentTester}
                              </Box>
                            </Tooltip>
                          </TableCell>
                          <TableCell sx={{ fontSize: "0.8rem", fontFamily: "monospace" }}>
                            <Tooltip title="Click to view Agents Trace logs">
                              <Box
                                sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5, '&:hover': { color: '#1976d2' } }}
                                onClick={() => handleOpenLogModal(row.traceId)}
                              >
                                {row.traceId}
                              </Box>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: "0.8rem", fontWeight: "bold", minWidth: "160px" }}>
                            {row.costAgentTester !== undefined ? `$${row.costAgentTester.toFixed(6)}` : "-"}
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: "0.8rem", fontWeight: "bold", minWidth: "140px" }}>
                            {row.costWidget !== undefined ? `$${row.costWidget.toFixed(6)}` : "-"}
                          </TableCell>
                          <TableCell sx={{ fontSize: "0.8rem" }}>{row.fixNotes}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
            )}
          </Paper>
        )}
      </Box>
      <Snackbar
        open={copyStatus.open}
        autoHideDuration={2000}
        onClose={() => setCopyStatus({ ...copyStatus, open: false })}
        message={copyStatus.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      <Dialog
        open={logModalOpen}
        onClose={() => setLogModalOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: { minHeight: '80vh' }
        }}
      >
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Agents Trace Logs</Typography>
          <IconButton
            aria-label="close"
            onClick={() => setLogModalOpen(false)}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <LogAgents initialTraceId={selectedLogTraceId} />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ChatTester;
